import { Text, Box, BoxProps } from "@govlaunch/web";
import React, { forwardRef, ReactNode } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import pick from "lodash/fp/pick";

interface ICollectionTabProps extends BoxProps {
  icon: ReactNode;
  label: string;
  count: number;
  selected: boolean;
  slug?: string;
  tab?: string;
}

const CollectionTab = forwardRef<any, ICollectionTabProps>(({ icon, label, count, selected, ...props }, ref) => {
  return (
    <Box
      as="a"
      color={selected ? "blue.600" : count > 0 ? "gray.900" : "gray.500"}
      display="flex"
      flexDirection={["column", "column", "row"]}
      alignItems="center"
      minWidth="135px"
      minHeight="72px"
      borderBottom="1px"
      borderBottomWidth="5px"
      py={2}
      px={["8px", "8px", "24px"]}
      borderColor={selected ? "blue.600" : "transparent"}
      ref={ref}
      _hover={{
        bg: count > 0 ? "blue.50" : undefined,
        cursor: count === 0 ? "default" : "pointer",
      }}
      {...props}
    >
      {icon}

      <Text fontWeight="semibold" fontSize={["md", "md", "lg"]} as="span" ml={2} whiteSpace="nowrap">
        {label} {count > 0 && `(${count})`}
      </Text>
    </Box>
  );
});

CollectionTab.displayName = "CollectionTab";

interface ICollectionsTabLinkProps {
  icon: ReactNode;
  tab?: string;
  label: string;
  slug: string;
  count: number;
  selected: boolean;
}

export default function CollectionTabLink({ icon, tab, label, slug, count, selected }: ICollectionsTabLinkProps) {
  const router = useRouter();
  const dataIntercomTarget = `Collection ${tab}`;

  if (count > 0) {
    return (
      <Link
        href={{
          pathname: "/collections/[collectionSlug]/[[...tab]]",
          query: {
            ...router.query,
            collectionSlug: slug,
            tab,
          },
        }}
        as={{
          pathname: `/collections/${slug}/${tab}`,
          query: pick(["countries", "tags"], router.query),
        }}
        passHref={true}
      >
        <CollectionTab
          icon={icon}
          label={label}
          count={count}
          selected={selected}
          data-intercom-target={dataIntercomTarget}
        />
      </Link>
    );
  }

  return (
    <CollectionTab
      icon={icon}
      label={label}
      count={count}
      selected={selected}
      data-intercom-target={dataIntercomTarget}
    />
  );
}
